import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Pause, Play } from "lucide-react";
import { cn } from "@/utils/cn";
import { durationLabel } from "../_lib/chat-labels";
import { FLAT_WAVEFORM } from "../_lib/waveform";

const SPEEDS = [1, 1.5, 2] as const;

/** One voice note plays at a time across the whole page. */
let nowPlaying: HTMLAudioElement | null = null;

interface VoiceNotePlayerProps {
  url: string;
  durationMs?: number | null;
  /** Loudness bars 0–100; older notes have none and draw flat. */
  waveform?: number[];
  isMine: boolean;
  /** The bubble's time and ticks, on the length/speed row. */
  footer?: ReactNode;
  /** Beside the length: before it (my note's played mic), after it (the unplayed dot). */
  beforeDuration?: ReactNode;
  afterDuration?: ReactNode;
  /** Playback started (each time). */
  onPlay?: () => void;
}

/**
 * Round play button, the note's waveform filling as it plays (click or drag
 * it to seek, arrow keys too), elapsed / total time and a 1× · 1.5× · 2× speed.
 */
export const VoiceNotePlayer = ({
  url,
  durationMs,
  waveform,
  isMine,
  footer,
  beforeDuration,
  afterDuration,
  onPlay,
}: VoiceNotePlayerProps) => {
  const { t } = useTranslation("chat");
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  // MediaRecorder WebM reports Infinity until fully read — prefer the sent length.
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const total = durationMs ? durationMs / 1000 : (mediaDuration ?? 0);
  const bars = waveform?.length ? waveform : FLAT_WAVEFORM;
  const progress = total > 0 ? Math.min(position / total, 1) : 0;

  // Smooth progress while playing (timeupdate only fires ~4× a second).
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      const a = audioRef.current;
      if (a) setPosition(a.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  useEffect(
    () => () => {
      if (nowPlaying === audioRef.current) nowPlaying = null;
    },
    [],
  );

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    if (nowPlaying && nowPlaying !== a) nowPlaying.pause();
    nowPlaying = a;
    a.playbackRate = speed;
    try {
      await a.play();
    } catch {
      setPlaying(false);
    }
  };

  const seekTo = (fraction: number) => {
    const a = audioRef.current;
    if (!a || total <= 0) return;
    const time = Math.max(0, Math.min(fraction, 1)) * total;
    a.currentTime = time;
    setPosition(time);
  };

  const seekFromPointer = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rtl = getComputedStyle(e.currentTarget).direction === "rtl";
    const x = rtl ? rect.right - e.clientX : e.clientX - rect.left;
    seekTo(x / rect.width);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (total <= 0) return;
    const step = 5 / total;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") seekTo(progress + step);
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") seekTo(progress - step);
    else return;
    e.preventDefault();
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  return (
    <div className="flex w-64 items-center gap-2.5 py-0.5">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onPlay={() => {
          setPlaying(true);
          onPlay?.();
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) setMediaDuration(d);
        }}
      />
      <button
        type="button"
        onClick={() => void toggle()}
        aria-label={playing ? t("voice.pause") : t("voice.play")}
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full transition-transform active:scale-95",
          isMine ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground",
        )}
      >
        {playing ? (
          <Pause className="size-4 fill-current" />
        ) : (
          <Play className="size-4 translate-x-px fill-current rtl:-translate-x-px rtl:-scale-x-100" />
        )}
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          role="slider"
          tabIndex={0}
          aria-label={t("voice.position")}
          aria-valuemin={0}
          aria-valuemax={Math.round(total)}
          aria-valuenow={Math.round(position)}
          aria-valuetext={durationLabel(position * 1000)}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            seekFromPointer(e);
          }}
          onPointerMove={(e) => {
            if (e.buttons) seekFromPointer(e);
          }}
          onKeyDown={onKeyDown}
          className="flex h-8 cursor-pointer touch-none items-center gap-[2px] outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-xs"
        >
          {bars.map((v, i) => {
            const played = (i + 0.5) / bars.length <= progress;
            return (
              <span
                key={i}
                className={cn(
                  "min-w-0 flex-1 rounded-full transition-colors",
                  played
                    ? isMine
                      ? "bg-primary-foreground"
                      : "bg-primary"
                    : isMine
                      ? "bg-primary-foreground/40"
                      : "bg-muted-foreground/30",
                )}
                style={{ height: `${Math.max(10, v)}%` }}
              />
            );
          })}
        </div>
        <div
          className={cn(
            "flex items-center gap-2 text-[11px] tabular-nums",
            isMine ? "text-primary-foreground/80" : "text-muted-foreground",
          )}
        >
          <span className="me-auto flex items-center gap-1">
            {beforeDuration}
            {durationLabel((playing || position > 0 ? position : total) * 1000)}
            {afterDuration}
          </span>
          <button
            type="button"
            onClick={cycleSpeed}
            aria-label={t("voice.speed", { speed })}
            className={cn(
              "rounded-full px-1.5 py-px text-[10px] font-semibold",
              isMine ? "bg-primary-foreground/20" : "bg-muted",
            )}
          >
            {`${speed}×`}
          </button>
          {footer}
        </div>
      </div>
    </div>
  );
};
